/** Where Segment keeps the identity, in both of the places it keeps it. */
const AJS_USER_ID_KEY = 'ajs_user_id';

/**
 * Segment writes the id as JSON, so a plain string arrives quoted — and the
 * cookie copy arrives URL-encoded on top of that. Older analytics.js wrote it
 * bare, which is why a failed parse falls through to the raw value rather than
 * discarding it.
 */
function parseStoredValue(raw: string | null): string | null {
  if (!raw) {
    return null;
  }

  let value = raw;

  try {
    value = decodeURIComponent(value);
  } catch {
    // Not encoded. Keep what we have.
  }

  try {
    const parsed: unknown = JSON.parse(value);

    if (parsed === null) {
      return null;
    }

    if (typeof parsed === 'string') {
      value = parsed;
    }
  } catch {
    // A bare string rather than JSON.
  }

  return value === '' ? null : value;
}

/**
 * The stored `ajs_user_id`, read the way analytics.js will read it.
 *
 * This exists because of a gap in the snippet's stub. `analytics.methods` lists
 * every method the queue can accept, and `user` is not one of them — so
 * `window.analytics.user()` does not exist until `analytics.min.js` finishes
 * loading, and on this site that load waits on Cookiebot. Anything asking the
 * library who this browser is during that window is told "nobody", which is not
 * the same as nobody: the id is sitting in the parent-domain cookie, and the
 * library adopts it the moment it hydrates — before flushing the queued calls.
 *
 * `/sign-up/website-callback` is the exposed case. It is a fresh page load that
 * identifies within a few hundred milliseconds of arriving, which is
 * comfortably inside the window.
 *
 * Duplicated from `extern-website`'s copy rather than shared: the two apps are
 * separate repos reading the same `.extern.com` cookie, and the format they
 * have to agree on is Segment's, not ours.
 *
 * localStorage before cookie, matching analytics.js's own precedence: the two
 * disagree exactly when one origin wrote locally and another wrote the shared
 * cookie, and the library would take the local one.
 */
export function readStoredSegmentUserId(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const fromLocalStorage = parseStoredValue(window.localStorage.getItem(AJS_USER_ID_KEY));

    if (fromLocalStorage) {
      return fromLocalStorage;
    }
  } catch {
    // Disabled, private mode, or over quota. The cookie is still worth a look.
  }

  const prefix = `${AJS_USER_ID_KEY}=`;
  const row = document.cookie.split('; ').find((cookie) => cookie.startsWith(prefix));

  return row ? parseStoredValue(row.slice(prefix.length)) : null;
}
